import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import MaskedInput from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';

import { FloatingInput, RocketAnimation } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { Modal } from 'src/components/index';
import { phoneNumberMaskSpaced, stripWhitespace } from 'src/helpers/formatting';
import { validateEmailTradeRoot, isValidEmail, isValidMobileNumber } from 'src/helpers/validation';
import { getRefundDetails, submitRefund, notificationMap } from 'src/services/refund.service';
import { IceCreamImage, InputErrorIcon } from 'src/assets/images';
import TealiumEvents from 'src/helpers/tealium-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import './refund-modal.scss';

const phases = {
  entry: 'phase1',
  success: 'phase2',
  failure: 'phase3',
};

export default function RefundModal(props) {
  const handleTechnicalError = useTechnicalErrorHandler();
  const [inputDanger, setInputDanger] = useState({
    refund: false,
    contactType: false,
    email: false,
    SMS: false,
  });
  const [, setRefundDetails] = useState();
  const [refundAmount, setRefundAmount] = useState(0);
  const [buttonActive, setButtonActive] = useState(false);
  const [phase, setPhase] = useState(phases.entry);
  const ref = useRef(null);

  function validateForm(values) {
    let newInputDanger = inputDanger;
    const errors = {};
    const emailTradeRootValidationError = validateEmailTradeRoot(values.email);
    const refundAmount = stripWhitespace(values.refund);
    if (parseFloat(refundAmount) > props.transactionDetails?.transactionAmount) {
      errors.refund = 'Maximum value of R' + props.transactionDetails?.transactionAmount;
      newInputDanger['refund'] = true;
    }
    if (!refundAmount || parseFloat(refundAmount) === 0) {
      errors.refund = 'Refund of more than R 0 required';
      newInputDanger['refund'] = true;
    }
    if (values.contactType === 'email') {
      if (emailTradeRootValidationError) {
        errors.email = values.email
          ? emailTradeRootValidationError
          : 'The email address entered is invalid';
      } else {
        const invalidEmail = !isValidEmail(values.email);
        errors.email = invalidEmail ? 'The email address entered is invalid' : null;
        newInputDanger['email'] = invalidEmail;
      }
    }
    if (values.contactType === 'sms') {
      const invalidMobileNumber = !isValidMobileNumber(stripWhitespace(values.mobileNumber));
      errors.mobileNumber = invalidMobileNumber ? 'The mobile number entered is invalid' : null;
      newInputDanger['mobileNumber'] = invalidMobileNumber;
    }
    setInputDanger(newInputDanger);

    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });
    setButtonActive(!anyErrors);

    return errors;
  }

  useEffect(() => {
    if (props.show && phase === phases.entry) {
      TealiumEvents.viewRefundsModal();
    }
  }, [props.show]);

  useEffect(() => {
    if (phase === phases.success) {
      TealiumEvents.successfulRefund(refundAmount);
    } else if (phase === phases.failure) {
      TealiumEvents.refundFailedModalLoad();
    }
  }, [phase]);

  const updateRefundDetails = async () => {
    if (props.transactionDetails) {
      const transactionFetchAbortController = new AbortController();
      const refundDetails = await getRefundDetails(
        transactionFetchAbortController,
        props.transactionDetails?.uniqueId,
        props.transactionDetails?.terminalId,
        props.transactionDetails?.PAN,
        handleTechnicalError,
        false,
        true,
      );
      if (!transactionFetchAbortController.signal.aborted) {
        if (refundDetails) {
          setRefundDetails(refundDetails);
        } else {
          setRefundDetails([]);
        }
        // setLoading(false);
      }
    }
  };

  useEffect(() => {
    updateRefundDetails();
  }, [props.transactionDetails]);

  const turnoverMask = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ' ',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    integerLimit: 9,
    allowNegative: false,
    allowLeadingZeroes: false,
    inputMode: 'numeric',
  });

  const submitFunction = async (values) => {
    setButtonActive(false);
    const res = await submitRefund(
      parseFloat(stripWhitespace(values.refund)),
      notificationMap[values.contactType],
      values.contactType === 'email' ? values.email : stripWhitespace(values.mobileNumber),
      props.transactionDetails?.uniqueId,
      handleTechnicalError,
      props.methodOfPayment,
    );
    if (res.ok) {
      setPhase(phases.success);
    } else {
      TealiumEvents.refundError(res.error);
      setPhase(phases.failure);
    }
  };

  const entryPhase = () => (
    <Formik
      initialValues={{
        terminalId: props.transactionDetails?.terminalId,
        card: props.transactionDetails?.PAN,
        refund: '',
        contactType: 'email',
        email: '',
        mobileNumber: '',
      }}
      validate={validateForm}
      validateOnChange={true}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      {({ values, touched, errors, handleReset }) => (
        <Form>
          <Modal
            show={props.show}
            cardStyle="RefundsModal"
            headerStyle="modal-card-head"
            bodyStyle="modal-body"
            footerStyle="modal-foot center-text"
            modalProps={{ className: 'no-scroll-container' }}
            backdropStyle="backdrop"
            header={() => (
              <>
                <span
                  className="button is-pulled-right close-button"
                  disabled={props.disabled}
                  onClick={() => {
                    if (!props.disabled) {
                      TealiumEvents.closeRefundsModalNormal();
                      props.onHide();
                      handleReset();
                      setPhase(phases.entry);
                    }
                  }}
                  automationid={`${props.automationIdPrefix}_Span_Close_Modal_Button`}
                />
                <div className="center-text is-hidden-tablet">
                  <h5 className="heading-four has-margin-y-5 has-color-black has-padding-left-3">
                    Refund Customer
                  </h5>
                </div>
              </>
            )}
            body={() => (
              <>
                <div className="center-text is-hidden-mobile">
                  <h5 className="heading-four has-margin-bottom-4 has-color-black">
                    Refund Customer
                  </h5>
                </div>
                <p className="label-text">Refund amount</p>
                <div
                  className={`wrapper price-wrapper ${
                    !errors.refund || !touched.refund ? 'no-error-padding' : ''
                  }`}
                >
                  <p
                    className={
                      'currency-symbol symbol-turnover ' + (values.refund ? '' : 'lighter')
                    }
                  >
                    R
                  </p>
                  <Field
                    name="refund"
                    render={({ field }) => (
                      <MaskedInput
                        {...field}
                        ref={ref}
                        id="refund"
                        mask={turnoverMask}
                        placeholder="0.00"
                        className={`input modal-input`}
                        automationid="Onboarding_Field_Turnover_Modal"
                      />
                    )}
                  />
                  <div className={`grey-line ${errors.refund && touched.refund ? 'error' : ''}`} />
                </div>
                {errors.refund && touched.refund && (
                  <div className="field-error">
                    <img className="error-image" src={InputErrorIcon} />
                    <span>{errors.refund}</span>
                  </div>
                )}
                <Field
                  name="terminalId"
                  type="text"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  placeholder="Terminal Id"
                  className="top-field"
                  disabled
                  // automationid="Onboarding_Lets_Get_Started_Field_Email"
                />
                <Field
                  name="card"
                  type="text"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  placeholder="Card"
                  className="bottom-field"
                  disabled
                  // automationid="Onboarding_Lets_Get_Started_Field_Email"
                />
                <div className="label-text ereceipt">Send e-receipt</div>
                <div role="group" className="columns is-mobile is-gapless">
                  <label
                    className="radio is-mobile"
                    automationid="Onboarding_Create_Account_Your_Details_Label_ID_Type_Value_ID"
                  >
                    <Field
                      name="contactType"
                      type="radio"
                      value="email"
                      className={`input radio-input ${
                        values.contactType === 'email' ? 'boxSelected' : ''
                      } email ${inputDanger['contactType'] ? 'create-account-input-danger' : ''}`}
                      automationid="Onboarding_Create_Account_Your_Details_Field_ID_Type_Value_ID"
                    />
                    <span
                      className={`radioText ${
                        values.contactType === 'email' ? 'fieldSelected' : ''
                      }`}
                      automationid="Onboarding_Create_Account_Your_Details_Span_ID_Type_Value_ID"
                    >
                      Email
                    </span>
                  </label>
                  <label
                    className="radio is-mobile"
                    automationid="Onboarding_Create_Account_Your_Details_Label_ID_Type_Value_Passport"
                  >
                    <Field
                      name="contactType"
                      type="radio"
                      value="sms"
                      className={`input radio-input ${
                        values.contactType === 'sms' ? 'boxSelected' : ''
                      } sms ${
                        inputDanger['contactType'] && touched.contactType
                          ? 'create-account-input-danger'
                          : ''
                      }`}
                      automationid="Onboarding_Create_Account_Your_Details_Field_ID_Type_Value_Passport"
                    />
                    <span
                      className={`radioText ${
                        values.contactType === 'sms' ? 'fieldSelected' : ''
                      } `}
                      automationid="Onboarding_Create_Account_Your_Details_Span_ID_Type_Value_Passport"
                    >
                      SMS
                    </span>
                  </label>
                </div>
                {values.contactType === 'email' && (
                  <Field
                    name="email"
                    type="text"
                    placeholder="Email address"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    // guide={!inMiniProgram}
                    fieldType="standard"
                    // automationid="Onboarding_Create_Account_Your_Details_Field_Id_Number"
                  />
                )}
                {values.contactType === 'sms' && (
                  <Field
                    name="mobileNumber"
                    type="text"
                    placeholder="Mobile number"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    mask={phoneNumberMaskSpaced}
                    // guide={!inMiniProgram}
                    fieldType="masked"
                    // automationid="Onboarding_Create_Account_Your_Details_Field_Id_Number"
                  />
                )}
              </>
            )}
            footer={() => (
              <div className="columns is-centered is-mobile has-padding-bottom-5">
                <div className="column">
                  <button
                    type="button"
                    disabled={!buttonActive}
                    className="button is-primary is-rounded has-padding-4 has-mobile-margin-top-0 has-margin-top-3 "
                    onClick={() => {
                      if (buttonActive) {
                        setRefundAmount(values.refund);
                        TealiumEvents.performRefund(values.refund);
                        submitFunction(values);
                      }
                    }}
                  >
                    Refund Customer
                  </button>
                </div>
              </div>
            )}
          />
        </Form>
      )}
    </Formik>
  );

  const successPhase = () => (
    <Modal
      show={props.show}
      cardStyle="RefundsModal"
      headerStyle="modal-head center-text"
      bodyStyle="modal-body"
      footerStyle="modal-foot center-text"
      backdropStyle="backdrop"
      header={() => <div></div>}
      body={() => (
        <div className="has-padding-3">
          <RocketAnimation isRefunds={true} />
          <div className="columns has-text-centered">
            <h5 className="heading-four has-margin-top-3">Customer refunded successfully</h5>
          </div>
        </div>
      )}
      footer={() => (
        <div className="columns is-centered is-mobile has-padding-bottom-5">
          <div className="column">
            <button
              className="button is-primary is-rounded has-mobile-margin-top-0 "
              onClick={() => {
                props.onHide();
                setPhase(phases.entry);
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    />
  );

  const failurePhase = () => (
    <Modal
      show={props.show}
      cardStyle="RefundsModal"
      headerStyle="modal-card-head"
      bodyStyle="modal-body"
      footerStyle="modal-foot center-text"
      backdropStyle="backdrop"
      header={() => (
        <span
          className="button is-pulled-right close-button"
          disabled={props.disabled}
          onClick={() => {
            TealiumEvents.closeRefundsModalFailure();
            props.onHide();
            setPhase(phases.entry);
          }}
          automationid={`${props.automationIdPrefix}_Span_Close_Modal_Button`}
        />
      )}
      body={() => (
        <div className="has-padding-3">
          <div className="ice-cream">
            <img src={IceCreamImage} />
          </div>
          <div className="has-text-centered">
            <h5 className="heading-four has-margin-top-3">Oops!</h5>
            <h5 className="heading-four has-margin-top-3">
              There was a problem processing the refund
            </h5>
          </div>
        </div>
      )}
      footer={() => (
        <div className="columns is-centered is-mobile has-padding-bottom-5">
          <div className="column">
            <button
              className="button is-primary is-rounded has-mobile-margin-top-0 "
              onClick={() => {
                TealiumEvents.refundFailedRetry(refundAmount);
                setPhase(phases.entry);
              }}
            >
              Try again
            </button>
          </div>
        </div>
      )}
    />
  );

  switch (phase) {
    case phases.entry:
      return entryPhase();
    case phases.success:
      return successPhase();
    case phases.failure:
      return failurePhase();
  }
}
